import {
  SegmentflowCore,
  type BatchAppData,
  type IdentifyEvent,
  type TrackEvent,
  type PageEvent,
  type AnalyticsConfig,
} from "@segmentflow/sdk-core";
import { uuidv7 } from "uuidv7";
import { getGlobalContext } from "./context.js";
import { getStorage } from "./storage.js";
import { getCookieStorage } from "./storage/cookie.js";
import { type Plugin } from "./plugins/plugin.js";

// ---------------------------------------------------------------------------
// Unified identity cookie (sf_id) — shared with PHP server-side code
// ---------------------------------------------------------------------------

/** Cookie name for the unified identity cookie */
const SF_ID_COOKIE = "sf_id";

/**
 * Shape of the decoded sf_id cookie.
 * Short keys to minimize cookie size (4 KB browser limit).
 *   a = anonymousId (UUIDv7, always present)
 *   u = userId      (when logged in, prefixed e.g. "wc_1")
 *   e = email       (when known)
 *   p = phone       (when known)
 */
interface SfIdData {
  a?: string;
  u?: string;
  e?: string;
  p?: string;
}

// Legacy storage keys (kept for migration, will be removed ~30 days post-M2)
const LEGACY_ANON_COOKIE = "sf_anon_id";
const LEGACY_USER_COOKIE = "sf_user_id";
const LEGACY_ANON_LS_KEY = "segmentflow_anonymous_id";
const LEGACY_USER_LS_KEY = "segmentflow_user_id";

// ---------------------------------------------------------------------------
// sf_id cookie helpers
// ---------------------------------------------------------------------------

function parseCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function readSfId(): SfIdData | null {
  const raw = parseCookieValue(SF_ID_COOKIE);
  if (!raw) return null;
  try {
    const json = atob(raw);
    const data = JSON.parse(json) as SfIdData;
    if (data && typeof data === "object" && data.a) return data;
  } catch {
    // Corrupt cookie — will be overwritten
  }
  return null;
}

function writeSfId(updates: Partial<SfIdData>): void {
  if (typeof document === "undefined") return;

  // Merge-on-write: read existing, overlay updates, re-encode.
  const existing = readSfId() ?? {};
  const merged: SfIdData = { ...existing, ...updates };

  // Strip empty values
  for (const key of Object.keys(merged) as (keyof SfIdData)[]) {
    if (!merged[key]) delete merged[key];
  }

  const encoded = btoa(JSON.stringify(merged));
  const expires = new Date(Date.now() + 730 * 86400000).toUTCString(); // 2 years
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${SF_ID_COOKIE}=${encodeURIComponent(encoded)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

// ---------------------------------------------------------------------------
// SegmentflowWeb
// ---------------------------------------------------------------------------

export interface SegmentflowWebConfig extends Omit<
  AnalyticsConfig,
  "issueRequest" | "uuid"
> {
  /**
   * API Host override
   */
  host?: string;
  /**
   * Custom request handler (optional override)
   */
  issueRequest?: AnalyticsConfig["issueRequest"];
}

export class SegmentflowWeb {
  private core: SegmentflowCore;
  private storage = getStorage();
  private cookieStorage = getCookieStorage();
  private anonymousId: string;
  private userId?: string;
  private plugins: Plugin[] = [];

  constructor(config: SegmentflowWebConfig) {
    // 1. Initialize Identity from unified sf_id cookie (preferred),
    //    then legacy cookies, then localStorage (oldest fallback).
    const sfId = readSfId();

    if (sfId?.a) {
      // Unified cookie exists — use it as source of truth.
      this.anonymousId = sfId.a;
      this.userId = sfId.u ?? undefined;
    } else {
      // Fall back to legacy cookies / localStorage for migration.
      this.anonymousId =
        this.cookieStorage.getItem(LEGACY_ANON_COOKIE) ??
        this.storage.getItem(LEGACY_ANON_LS_KEY) ??
        uuidv7();

      const storedUserId =
        this.cookieStorage.getItem(LEGACY_USER_COOKIE) ??
        this.storage.getItem(LEGACY_USER_LS_KEY);
      if (storedUserId) {
        this.userId = storedUserId;
      }

      // Migrate: write the unified cookie so PHP can read it immediately.
      const sfIdInit: SfIdData = { a: this.anonymousId };
      if (this.userId) sfIdInit.u = this.userId;
      writeSfId(sfIdInit);
    }

    // Keep writing legacy cookies during transition period (~30 days).
    this.cookieStorage.setItem(LEGACY_ANON_COOKIE, this.anonymousId);
    this.storage.setItem(LEGACY_ANON_LS_KEY, this.anonymousId);
    if (this.userId) {
      this.cookieStorage.setItem(LEGACY_USER_COOKIE, this.userId);
      this.storage.setItem(LEGACY_USER_LS_KEY, this.userId);
    }

    // 2. Initialize Core
    this.core = new SegmentflowCore({
      ...config,
      uuid: uuidv7, // Use UUIDv7 for time-sortable IDs
      issueRequest: config.issueRequest ?? this.defaultIssueRequest.bind(this),
      // Use window timers
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (id) => window.clearTimeout(id),
    });
  }

  /** Write key used to initialise this SDK instance */
  public get writeKey(): string {
    return this.core.writeKey;
  }

  /** API host used by this SDK instance */
  public get host(): string {
    return this.core.host;
  }

  /** Returns the anonymous ID for this browser session */
  public getAnonymousId(): string {
    return this.anonymousId;
  }

  private async defaultIssueRequest(
    data: BatchAppData,
    config: { writeKey: string; host: string },
  ): Promise<void> {
    const url = `${config.host}/api/v1/ingest/batch`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      // keepalive: true allows the browser to complete this request even if the
      // page navigates away or the connection is recycled before the 500ms batch
      // timer fires. Standard practice for analytics SDKs to prevent event loss.
      // Note: keepalive fetch has a 64KB body limit per spec; a typical batch of
      // 1-5 events is well under 1KB so this limit is not a concern in practice.
      keepalive: true,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "Unknown error");
      throw new Error(`Segmentflow API Error ${response.status}: ${text}`);
    }
  }

  /**
   * Enriches events with context and identity
   */
  private enrich<T extends Record<string, unknown>>(
    params: T,
  ): T & {
    userId: string | undefined;
    anonymousId: string;
    context: Record<string, unknown>;
  } {
    return {
      ...params,
      userId: this.userId,
      anonymousId: this.anonymousId,
      source: "sdk",
      context: {
        ...getGlobalContext(),
        ...(params.context as Record<string, unknown> | undefined),
      },
    };
  }

  public identify(
    params: Omit<
      IdentifyEvent,
      "type" | "messageId" | "timestamp" | "anonymousId"
    >,
  ) {
    // Update local state
    if (params.userId) {
      this.userId = params.userId;
      // Legacy persistence (transition period)
      this.cookieStorage.setItem(LEGACY_USER_COOKIE, this.userId);
      this.storage.setItem(LEGACY_USER_LS_KEY, this.userId);
    }

    // Update the unified sf_id cookie with userId and email (if present).
    const sfIdUpdates: Partial<SfIdData> = {};
    if (params.userId) sfIdUpdates.u = params.userId;
    const email = (params.traits as Record<string, unknown> | undefined)?.email;
    if (typeof email === "string" && email) sfIdUpdates.e = email;
    if (Object.keys(sfIdUpdates).length > 0) {
      writeSfId(sfIdUpdates);
    }

    const enriched = this.enrich(params);
    this.plugins.forEach((p) => p.identify && p.identify(enriched));
    this.core.identify(enriched);
  }

  public async register(plugin: Plugin): Promise<void> {
    await plugin.load(this);
    this.plugins.push(plugin);
  }

  public reset() {
    this.userId = undefined;
    // Clear legacy
    this.storage.removeItem(LEGACY_USER_LS_KEY);
    this.cookieStorage.removeItem(LEGACY_USER_COOKIE);
    // Generate new anonymous ID
    this.anonymousId = uuidv7();
    // Write new unified cookie
    writeSfId({ a: this.anonymousId });
    // Legacy persistence (transition period)
    this.storage.setItem(LEGACY_ANON_LS_KEY, this.anonymousId);
    this.cookieStorage.setItem(LEGACY_ANON_COOKIE, this.anonymousId);
  }

  public track(
    params: Omit<
      TrackEvent,
      "type" | "messageId" | "timestamp" | "userId" | "anonymousId"
    >,
  ) {
    const enriched = this.enrich(params);
    this.plugins.forEach((p) => p.track && p.track(enriched));
    this.core.track(enriched);
  }

  public page(
    params?: Omit<
      PageEvent,
      "type" | "messageId" | "timestamp" | "userId" | "anonymousId"
    >,
  ) {
    const enriched = this.enrich(params ?? {});
    if (!enriched.name) enriched.name = document.title;

    this.plugins.forEach((p) => p.page && p.page(enriched));
    this.core.page(enriched);
  }

  public async flush(): Promise<void> {
    return this.core.flush();
  }
}

// Export cookie helpers for use by plugins (e.g., WooCommerce checkout email capture)
export { writeSfId, readSfId, type SfIdData };
